import React, { useState, useEffect } from "react";
import { AlertTriangle, CreditCard } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import LocationShare from "../components/Location/LocationShare";
import "./ProfilePage.css";

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    description: "",
    certification: "",
    bank_rib: "",
    mobile_money: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        phone: user.phone || "",
        address: user.address || "",
        city: user.city || "",
        postal_code: user.postal_code || "",
        description: user.description || "",
        certification: user.certification || "",
        bank_rib: user.bank_rib || "",
        mobile_money: user.mobile_money || "",
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    // Validation des champs obligatoires
    if (!formData.address || !formData.address.trim()) {
      setError("L'adresse est obligatoire");
      setLoading(false);
      return;
    }

    if (!formData.city || !formData.city.trim()) {
      setError("La ville est obligatoire");
      setLoading(false);
      return;
    }

    if (!formData.phone || !formData.phone.trim()) {
      setError("Le téléphone est obligatoire");
      setLoading(false);
      return;
    }

    // Validation pour les éleveurs : mobile money obligatoire
    if (user.role === "breeder" && (!formData.mobile_money || !formData.mobile_money.trim())) {
      setError("Le numéro mobile money est obligatoire pour les éleveurs afin de recevoir les paiements");
      setLoading(false);
      return;
    }

    const result = await updateUser(formData);
    
    if (result.success) {
      setSuccess(true);
      // Faire disparaître le message de succès après 3 secondes
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  if (!user) {
    return (
      <div className="container main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container main-content">
      <div className="page-header">
        <h1>Mon profil</h1>
        <p>Gérez vos informations personnelles</p>
      </div>

      <div className="profile-container">
        <div className="profile-sidebar card">
          <div className="profile-avatar">
            <div className="avatar-circle">
              {user.name.charAt(0).toUpperCase()}
            </div>
          </div>
          <h2>{user.name}</h2>
          <p className="profile-email">{user.email}</p>
          <div className="profile-role">
            <span className={`badge ${user.role === "breeder" ? "badge-primary" : "badge-secondary"}`}>
              {user.role === "breeder" ? "Éleveur" : "Restaurateur"}
            </span>
          </div>
          {user.certification && (
            <div className="profile-certification">
              <strong>Certification:</strong> {user.certification}
            </div>
          )}
          {user.role === "breeder" && (
            <div className="profile-payment-info" style={{ marginTop: "1rem", padding: "1rem", background: "var(--background)", borderRadius: "var(--radius-md)" }}>
              <h3 style={{ fontSize: "0.9rem", marginBottom: "0.5rem", color: "var(--text-secondary)" }}>Informations de paiement</h3>
              {user.mobile_money && (
                <div style={{ marginBottom: "0.5rem" }}>
                  <strong style={{ fontSize: "0.85rem" }}>Mobile Money:</strong>
                  <div style={{ fontSize: "0.9rem", color: "var(--text-primary)" }}>{user.mobile_money}</div>
                </div>
              )}
              {user.bank_rib && (
                <div>
                  <strong style={{ fontSize: "0.85rem" }}>RIB:</strong>
                  <div style={{ fontSize: "0.9rem", color: "var(--text-primary)", wordBreak: "break-all" }}>{user.bank_rib}</div>
                </div>
              )}
              {!user.mobile_money && (
                <div className="icon-inline" style={{ fontSize: "0.85rem", color: "var(--warning)", marginTop: "0.5rem" }}>
                  <AlertTriangle size={13} strokeWidth={2.25} /> Aucun numéro mobile money configuré
                </div>
              )}
            </div>
          )}
        </div>

        <div className="profile-main card">
          <h2>Informations</h2>
          
          {success && (
            <div className="alert alert-success">
              Profil mis à jour avec succès !
            </div>
          )}

          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit} data-tour="profile-form">
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Nom / Nom de l'établissement
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-control"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">
                Téléphone <span style={{color: 'red'}}>*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                className="form-control"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+226 70 70 70 70"
              />
            </div>

            <LocationShare
              value={formData.address}
              onChange={handleChange}
              placeholder="Adresse complète"
              label="Adresse"
              required={true}
              name="address"
            />

            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "1rem" }}>
              <div className="form-group">
                <label htmlFor="city" className="form-label">
                  Ville <span style={{color: 'red'}}>*</span>
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  className="form-control"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  placeholder="Ouagadougou"
                />
              </div>

              <div className="form-group">
                <label htmlFor="postal_code" className="form-label">
                  Code postal
                </label>
                <input
                  type="text"
                  id="postal_code"
                  name="postal_code"
                  className="form-control"
                  value={formData.postal_code}
                  onChange={handleChange}
                  placeholder="01 BP 1234"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description" className="form-label">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                className="form-control"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                placeholder="Parlez de vous ou de votre établissement..."
              />
            </div>

            {user.role === "breeder" && (
              <>
                <div className="form-group">
                  <label htmlFor="certification" className="form-label">
                    Certifications
                  </label>
                  <input
                    type="text"
                    id="certification"
                    name="certification"
                    className="form-control"
                    value={formData.certification}
                    onChange={handleChange}
                    placeholder="Bio, Label Rouge, etc."
                  />
                </div>

                <div style={{ marginTop: "2rem", padding: "1.5rem", background: "linear-gradient(135deg, rgba(var(--primary-rgb), 0.1) 0%, rgba(23, 162, 184, 0.1) 100%)", borderRadius: "var(--radius-md)", borderLeft: "4px solid var(--primary)" }} data-tour="profile-mobile-money">
                  <h3 className="icon-title" style={{ marginBottom: "1rem", fontSize: "1.1rem" }}><CreditCard size={18} strokeWidth={2.25} /> Informations de paiement</h3>
                  
                  <div className="form-group">
                    <label htmlFor="mobile_money" className="form-label">
                      Numéro Mobile Money <span style={{color: 'red'}}>*</span>
                    </label>
                    <input
                      type="tel"
                      id="mobile_money"
                      name="mobile_money"
                      className="form-control"
                      value={formData.mobile_money}
                      onChange={handleChange}
                      required
                      placeholder="+226 70 70 70 70"
                    />
                    <small className="icon-inline" style={{color: '#666', fontSize: '0.85rem', marginTop: '0.25rem', display: 'flex', alignItems: 'flex-start'}}>
                      <AlertTriangle size={13} strokeWidth={2.25} style={{flexShrink: 0, marginTop: '0.15rem'}} /> <strong>Important :</strong> Ce numéro est nécessaire pour recevoir les paiements de la plateforme. Les fonds vous seront versés sur ce compte mobile money lorsque le paiement sera libéré après validation de la livraison.
                    </small>
                  </div>
                  
                  <div className="form-group" style={{ marginTop: "1rem" }}>
                    <label htmlFor="bank_rib" className="form-label">
                      RIB (Relevé d'Identité Bancaire) - Optionnel
                    </label>
                    <input
                      type="text"
                      id="bank_rib"
                      name="bank_rib"
                      className="form-control"
                      value={formData.bank_rib}
                      onChange={handleChange}
                      placeholder="Code banque - Code guichet - Numéro de compte - Clé RIB"
                    />
                    <small style={{color: '#666', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block'}}>
                      Format : 12345 67890 12345678901 12 (pour les virements bancaires optionnels)
                    </small>
                  </div>
                </div>
              </>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading}
              style={{ width: "100%" }}
            >
              {loading ? "Mise à jour..." : "Mettre à jour"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;

